import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

function maintSearchBlob(item) {
  return `${item.domain_name || ''} ${item.customer_name || ''} ${item.id || ''}`.toLowerCase();
}

export function BexioReconMaintenancePicker({
  items,
  value,
  onChange,
  disabled,
  hintText,
  preferredIds = [],
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const preferredSet = useMemo(
    () => new Set((preferredIds || []).filter(Boolean)),
    [preferredIds]
  );

  const selected = useMemo(
    () => items.find((d) => d.id === value) || null,
    [items, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? items.filter((d) => maintSearchBlob(d).includes(q))
      : items.slice();
    base.sort((a, b) => {
      const ap = preferredSet.has(a.id) ? 0 : 1;
      const bp = preferredSet.has(b.id) ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return String(a.domain_name || '').localeCompare(String(b.domain_name || ''));
    });
    return base.slice(0, 80);
  }, [items, query, preferredSet]);

  const label = selected
    ? `${selected.domain_name || 'Untitled'}${selected.customer_name ? ` · ${selected.customer_name}` : ''}`
    : 'Select maintenance site…';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-7 min-w-0 flex-1 justify-between px-2 text-[11px] font-normal"
          data-testid="bexio-recon-maint-picker"
          title={hintText || 'Search by domain or customer'}
        >
          <span className={cn('truncate', !selected && 'text-gray-400')}>{label}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[80] w-80 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search domain or customer…"
            value={query}
            onValueChange={setQuery}
            data-testid="bexio-recon-maint-search"
          />
          <CommandList>
            <CommandEmpty>No matching maintenance sites.</CommandEmpty>
            <CommandGroup>
              {filtered.map((item) => (
                <CommandItem
                  key={item.id}
                  value={item.id}
                  onSelect={() => {
                    onChange(item.id);
                    setOpen(false);
                    setQuery('');
                  }}
                  data-testid={`bexio-recon-maint-option-${item.id}`}
                >
                  <Check
                    className={cn(
                      'mr-2 h-3.5 w-3.5 shrink-0',
                      value === item.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-xs font-medium text-gray-900">
                      {item.domain_name || 'Untitled'}
                      {preferredSet.has(item.id) ? (
                        <span className="ml-1 text-[10px] font-semibold text-amber-700">suggested</span>
                      ) : null}
                    </span>
                    <span className="truncate text-[10px] text-gray-500">
                      {item.customer_name || 'No customer'}
                    </span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
